import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fixImageUrl, fixImageUrls } from '../utils/imageUtils';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    property_type: 'Appartement',
    listing_type: 'Vente',
    status: 'À vendre',
    location: '',
    bedrooms: '',
    bathrooms: '',
    surface: '',
    peb: '',
    images: [],
    available: true,
    show_in_catalog: true,
    show_in_rentals: false
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploadingImage(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Cloudinary returns full URL, use it directly
      const imageUrl = response.data.url;
      setFormData(prev => ({...prev, images: [...prev.images, imageUrl]}));
      toast.success('Image ajoutée');
      // Reset file input
      e.target.value = '';
    } catch (error) {
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingImage(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort by display_order and fix image URLs
      const sorted = response.data
        .map(prop => ({
          ...prop,
          images: fixImageUrls(prop.images || [])
        }))
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setProperties(sorted);
    } catch (error) {
      toast.error('Erreur lors du chargement des biens');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveProperty = async (index, direction) => {
    const newProperties = [...properties];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newProperties.length) return;
    
    // Swap properties
    [newProperties[index], newProperties[newIndex]] = [newProperties[newIndex], newProperties[index]];
    
    // Update display_order for all properties
    const updatedOrders = newProperties.map((prop, idx) => ({
      id: prop.id,
      display_order: idx
    }));
    
    setProperties(newProperties);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/properties/reorder`, updatedOrders, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ordre mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de l\'ordre');
      fetchProperties(); // Revert on error
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        surface: parseFloat(formData.surface),
        peb: formData.peb || null,
        images: formData.images.length > 0 ? formData.images : [
          'https://images.unsplash.com/photo-1694008384956-d55500529055',
          'https://images.unsplash.com/photo-1527665830090-864a163d49ab'
        ]
      };

      if (editingProperty) {
        await axios.put(`${API}/properties/${editingProperty.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bien modifié avec succès');
      } else {
        await axios.post(`${API}/properties`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bien ajouté avec succès');
      }

      setDialogOpen(false);
      setEditingProperty(null);
      setFormData({
        title: '',
        description: '',
        price: '',
        property_type: 'Appartement',
        listing_type: 'Vente',
        status: 'À vendre',
        location: '',
        bedrooms: '',
        bathrooms: '',
        surface: '',
        peb: '',
        images: [],
        available: true,
        show_in_catalog: true,
        show_in_rentals: false
      });
      fetchProperties();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      description: property.description,
      price: property.price.toString(),
      property_type: property.property_type,
      listing_type: property.listing_type || 'Vente',
      status: property.status || 'À vendre',
      location: property.location,
      bedrooms: property.bedrooms.toString(),
      bathrooms: property.bathrooms.toString(),
      surface: property.surface.toString(),
      peb: property.peb || '',
      images: property.images || [],
      available: property.available,
      show_in_catalog: property.show_in_catalog !== undefined ? property.show_in_catalog : true,
      show_in_rentals: property.show_in_rentals !== undefined ? property.show_in_rentals : false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce bien?')) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bien supprimé');
      fetchProperties();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div data-testid="loading-spinner" className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-20 pb-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="pt-12 flex items-center justify-between">
          <div className="space-y-4">
            <h1 className="text-5xl md:text-6xl font-serif tracking-tight leading-none">Administration</h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground">Gestion des biens immobiliers</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-property-button"
                onClick={() => {
                  setEditingProperty(null);
                  setNewImageUrl('');
                  setFormData({
                    title: '',
                    description: '',
                    price: '',
                    property_type: 'Appartement',
                    listing_type: 'Vente',
                    status: 'À vendre',
                    location: '',
                    bedrooms: '',
                    bathrooms: '',
                    surface: '',
                    peb: '',
                    images: [],
                    available: true,
                    show_in_catalog: true,
                    show_in_rentals: false
                  });
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 text-xs uppercase tracking-widest font-semibold transition-all duration-300"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un bien
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif">
                  {editingProperty ? 'Modifier le bien' : 'Ajouter un bien'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} data-testid="property-form" className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Titre</label>
                  <Input
                    data-testid="property-form-title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Description</label>
                  <Textarea
                    data-testid="property-form-description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Type</label>
                    <Select value={formData.property_type} onValueChange={(value) => setFormData({...formData, property_type: value})}>
                      <SelectTrigger data-testid="property-form-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Appartement">Appartement</SelectItem>
                        <SelectItem value="Maison">Maison</SelectItem>
                        <SelectItem value="Villa">Villa</SelectItem>
                        <SelectItem value="Studio">Studio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Catégorie</label>
                    <Select value={formData.listing_type} onValueChange={(value) => setFormData({...formData, listing_type: value})}>
                      <SelectTrigger data-testid="property-form-listing-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Vente">Vente</SelectItem>
                        <SelectItem value="Location">Location</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Prix (€)</label>
                    <Input
                      data-testid="property-form-price"
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Statut</label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger data-testid="property-form-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="À vendre">À vendre</SelectItem>
                        <SelectItem value="Sous option">Sous option</SelectItem>
                        <SelectItem value="Vendu">Vendu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Localisation</label>
                  <Input
                    data-testid="property-form-location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    required
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Chambres</label>
                    <Input
                      data-testid="property-form-bedrooms"
                      type="number"
                      value={formData.bedrooms}
                      onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Salles de bain</label>
                    <Input
                      data-testid="property-form-bathrooms"
                      type="number"
                      value={formData.bathrooms}
                      onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Surface (m²)</label>
                    <Input
                      data-testid="property-form-surface"
                      type="number"
                      value={formData.surface}
                      onChange={(e) => setFormData({...formData, surface: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">PEB (Certificat énergétique)</label>
                  <Select value={formData.peb || ''} onValueChange={(value) => setFormData({...formData, peb: value})}>
                    <SelectTrigger data-testid="property-form-peb">
                      <SelectValue placeholder="Sélectionner le PEB" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A++">A++</SelectItem>
                      <SelectItem value="A+">A+</SelectItem>
                      <SelectItem value="A">A</SelectItem>
                      <SelectItem value="B">B</SelectItem>
                      <SelectItem value="C">C</SelectItem>
                      <SelectItem value="D">D</SelectItem>
                      <SelectItem value="E">E</SelectItem>
                      <SelectItem value="F">F</SelectItem>
                      <SelectItem value="G">G</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Images</label>
                  <div className="space-y-2">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-secondary rounded">
                        <span className="text-xs text-muted-foreground w-6">{idx + 1}.</span>
                        <img src={img} alt={`Image ${idx + 1}`} className="w-16 h-12 object-cover rounded" />
                        <span className="flex-1 text-sm truncate">{img.split('/').pop()}</span>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={idx === 0}
                            onClick={() => {
                              const newImages = [...formData.images];
                              [newImages[idx - 1], newImages[idx]] = [newImages[idx], newImages[idx - 1]];
                              setFormData({...formData, images: newImages});
                            }}
                            title="Monter"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={idx === formData.images.length - 1}
                            onClick={() => {
                              const newImages = [...formData.images];
                              [newImages[idx], newImages[idx + 1]] = [newImages[idx + 1], newImages[idx]];
                              setFormData({...formData, images: newImages});
                            }}
                            title="Descendre"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newImages = formData.images.filter((_, i) => i !== idx);
                              setFormData({...formData, images: newImages});
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                        disabled={uploadingImage}
                      />
                      <label 
                        htmlFor="image-upload"
                        className="flex-1 cursor-pointer"
                      >
                        <div className="w-full border-2 border-dashed border-border hover:border-primary transition-colors rounded p-4 text-center">
                          {uploadingImage ? (
                            <span className="text-sm text-muted-foreground">Upload en cours...</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">Cliquez pour ajouter une image (JPG, PNG)</span>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-secondary rounded">
                  <h4 className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">Visibilité du bien</h4>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_in_catalog}
                        onChange={(e) => setFormData({...formData, show_in_catalog: e.target.checked})}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                        data-testid="property-form-show-catalog"
                      />
                      <div>
                        <div className="text-sm font-semibold">Afficher dans le catalogue public</div>
                        <div className="text-xs text-muted-foreground">Les visiteurs verront ce bien sur la page "Biens"</div>
                      </div>
                    </label>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_in_rentals}
                        onChange={(e) => setFormData({...formData, show_in_rentals: e.target.checked})}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                        data-testid="property-form-show-rentals"
                      />
                      <div>
                        <div className="text-sm font-semibold">Disponible pour la gestion locative</div>
                        <div className="text-xs text-muted-foreground">Ce bien pourra être assigné à des clients en gestion locative</div>
                      </div>
                    </label>
                  </div>
                </div>

                <Button
                  data-testid="property-form-submit"
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-sm uppercase tracking-widest font-semibold"
                >
                  {editingProperty ? 'Modifier' : 'Ajouter'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div data-testid="properties-table" className="bg-white border border-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-secondary">
              <tr>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Ordre</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Bien</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Type</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Localisation</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Prix</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((property, index) => (
                <tr key={property.id} data-testid={`property-row-${property.id}`} className="border-t border-border hover:bg-secondary/50 transition-colors">
                  <td className="p-4">
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={index === 0}
                        onClick={() => handleMoveProperty(index, 'up')}
                        title="Monter"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={index === properties.length - 1}
                        onClick={() => handleMoveProperty(index, 'down')}
                        title="Descendre"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={property.images[0] || 'https://images.unsplash.com/photo-1520608421741-68228b76b6df'}
                        alt={property.title}
                        className="w-16 h-12 object-cover"
                      />
                      <div>
                        <span className="font-semibold block">{property.title}</span>
                        <div className="flex gap-1 mt-1">
                          {property.show_in_catalog && (
                            <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded">Catalogue</span>
                          )}
                          {property.show_in_rentals && (
                            <span className="text-xs px-2 py-0.5 bg-accent/20 text-accent rounded">Gestion</span>
                          )}
                          {property.status && property.status !== 'À vendre' && (
                            <span className={`text-xs px-2 py-0.5 rounded ${property.status === 'Vendu' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                              {property.status}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">{property.property_type}</td>
                  <td className="p-4">{property.location}</td>
                  <td className="p-4 font-semibold">€{property.price.toLocaleString()}</td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Button
                        data-testid={`edit-property-${property.id}`}
                        onClick={() => handleEdit(property)}
                        size="sm"
                        variant="ghost"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-property-${property.id}`}
                        onClick={() => handleDelete(property.id)}
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;